import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import CONFIG from '../config';
import { show_alerta, obtenerOpcionesValorativa } from '../funciones';
import './css/MonitoreoEstadisticas.css';

// Chart.js e integración con react-chartjs-2
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function MonitoreoEstadisticas({ acceder, rol, configuracion }) {
  const navigate = useNavigate();

  // ── 1. Control de Seguridad por Rol (Permitidos: 1, 2, 3, 4) ──
  useEffect(() => {
    const rolActual = String(rol || localStorage.getItem('loggeduserRolId') || localStorage.getItem('loggeduserRol') || '');
    const rolesPermitidos = ['1', '2', '3', '4'];
    if (rolActual && !rolesPermitidos.includes(rolActual)) {
      show_alerta('Acceso denegado. Este módulo es exclusivo para Administradores, Equipo Directivo y Secretaría.', 'warning');
      navigate('/Principal');
    }
  }, [rol, navigate]);

  // ── 2. Estados de Filtros en Cascada ──
  const [filtroAnio, setFiltroAnio] = useState('');
  const [filtroFormacion, setFiltroFormacion] = useState('TODOS');
  const [filtroOrden, setFiltroOrden] = useState('TODOS');
  const [filtroDivision, setFiltroDivision] = useState('TODOS');
  const [filtroInstancia, setFiltroInstancia] = useState('TODAS');
  const [filtroUmbralValorativo, setFiltroUmbralValorativo] = useState('');

  // Estado para el Modal de detalle de materias en riesgo por estudiante
  const [estudianteSeleccionadoModal, setEstudianteSeleccionadoModal] = useState(null);
  const [mostrarModalEspacios, setMostrarModalEspacios] = useState(false);

  // ── 3. Estados de Datos Brutos desde Backend ──
  const [loading, setLoading] = useState(true);
  const [cohortesRaw, setCohortesRaw] = useState([]);
  const [combinacionesRaw, setCombinacionesRaw] = useState([]);
  const [opcionesInstanciasRaw, setOpcionesInstanciasRaw] = useState([]);

  const [estudiantesRaw, setEstudiantesRaw] = useState([]);
  const [valoracionesRaw, setValoracionesRaw] = useState([]);
  const [asistenciasRaw, setAsistenciasRaw] = useState([]);
  const [cursosRaw, setCursosRaw] = useState([]);

  // ── 4. Carga Inicial de Estructura (Cohortes, Opciones y Filtros) ──
  useEffect(() => {
    const loggeduserNivelId = localStorage.getItem('loggeduserNivelId');
    setLoading(true);

    Promise.all([
      // A. Cohortes según el nivel del usuario
      axios.get(`${CONFIG.API_URL}/operarCohortes.php?accion=listar${loggeduserNivelId ? `&id_nivel=${loggeduserNivelId}` : ''}`),
      // B. Opciones de estructura (combinaciones de ciclo, formación, orden, división)
      axios.post(`${CONFIG.API_URL}/operarReportes.php`, {
        modo: 'filtrosOpciones',
        id_nivel: loggeduserNivelId
      })
    ])
      .then(([resCoh, resRep]) => {
        const cohortes = (resCoh.data && resCoh.data.success && Array.isArray(resCoh.data.cohortes)) ? resCoh.data.cohortes : [];
        const combinaciones = (resRep.data && !resRep.data.error && Array.isArray(resRep.data.combinaciones)) ? resRep.data.combinaciones : [];

        setCohortesRaw(cohortes);
        setCombinacionesRaw(combinaciones);
      })
      .catch(err => console.error('Error al cargar estructura de filtros:', err))
      .finally(() => setLoading(false));
  }, []);

  // ── 5. CÁLCULO DE OPCIONES DISPONIBLES EN CASCADA ──

  // Paso 1: Años/Ciclos disponibles (ÚNICOS, según el nivel del usuario)
  const opcionesAnios = useMemo(() => {
    const aniosSet = new Set();
    cohortesRaw.forEach(c => {
      if (c.año) aniosSet.add(String(c.año));
    });
    combinacionesRaw.forEach(c => {
      if (c.ciclo) aniosSet.add(String(c.ciclo));
    });
    return Array.from(aniosSet).sort((a, b) => b - a);
  }, [cohortesRaw, combinacionesRaw]);

  // Auto-selección inicial del año más reciente
  useEffect(() => {
    if (opcionesAnios.length > 0 && (!filtroAnio || !opcionesAnios.includes(filtroAnio))) {
      setFiltroAnio(opcionesAnios[0]);
    }
  }, [opcionesAnios, filtroAnio]);

  // Paso 2: Formaciones habilitadas en el Año/Ciclo seleccionado
  const opcionesFormaciones = useMemo(() => {
    if (!filtroAnio) return [];
    const formSet = new Set();

    cohortesRaw.forEach(c => {
      if (String(c.año) === String(filtroAnio) && c.nombre_formacion) {
        formSet.add(c.nombre_formacion);
      }
    });
    combinacionesRaw.forEach(c => {
      if (String(c.ciclo) === String(filtroAnio) && c.nivel_nombre) {
        formSet.add(c.nivel_nombre);
      }
    });

    return Array.from(formSet).sort();
  }, [cohortesRaw, combinacionesRaw, filtroAnio]);

  // Ajustar filtroFormacion si deja de existir en la nueva selección de Año
  useEffect(() => {
    if (filtroFormacion !== 'TODOS' && !opcionesFormaciones.includes(filtroFormacion)) {
      setFiltroFormacion('TODOS');
    }
  }, [opcionesFormaciones, filtroFormacion]);

  // Paso 3: Años/Órdenes correspondientes a la Formación seleccionada
  const opcionesOrdenes = useMemo(() => {
    if (!filtroAnio) return [];
    const ordSet = new Set();

    combinacionesRaw.forEach(c => {
      const matchAnio = String(c.ciclo) === String(filtroAnio);
      const matchForm = filtroFormacion === 'TODOS' || String(c.nivel_nombre).toLowerCase() === String(filtroFormacion).toLowerCase();
      if (matchAnio && matchForm && c.orden) {
        ordSet.add(c.orden);
      }
    });

    return Array.from(ordSet).sort();
  }, [combinacionesRaw, filtroAnio, filtroFormacion]);

  // Ajustar filtroOrden al cambiar Formación
  useEffect(() => {
    if (filtroOrden !== 'TODOS' && !opcionesOrdenes.includes(filtroOrden)) {
      setFiltroOrden('TODOS');
    }
  }, [opcionesOrdenes, filtroOrden]);

  // Paso 4: Divisiones correspondientes al Año/Orden seleccionado (si no es TODOS)
  const opcionesDivisiones = useMemo(() => {
    if (!filtroAnio || filtroOrden === 'TODOS') return [];
    const divSet = new Set();

    combinacionesRaw.forEach(c => {
      const matchAnio = String(c.ciclo) === String(filtroAnio);
      const matchForm = filtroFormacion === 'TODOS' || String(c.nivel_nombre).toLowerCase() === String(filtroFormacion).toLowerCase();
      const matchOrd = String(c.orden).toLowerCase() === String(filtroOrden).toLowerCase();

      if (matchAnio && matchForm && matchOrd && c.division) {
        divSet.add(c.division);
      }
    });

    return Array.from(divSet).sort();
  }, [combinacionesRaw, filtroAnio, filtroFormacion, filtroOrden]);

  // Resetear División al cambiar Orden o si no está en las opciones válidas
  useEffect(() => {
    if (filtroOrden === 'TODOS') {
      setFiltroDivision('TODOS');
    } else if (filtroDivision !== 'TODOS' && !opcionesDivisiones.includes(filtroDivision)) {
      setFiltroDivision('TODOS');
    }
  }, [filtroOrden, opcionesDivisiones, filtroDivision]);

  // ── 6. Carga de Datos Estadísticos e Instancias al Cambiar Año / Cohorte / Formación ──
  useEffect(() => {
    if (!filtroAnio) return;
    setLoading(true);

    const loggeduserNivelId = localStorage.getItem('loggeduserNivelId');

    // Identificar cohorte o id relevante para peticiones
    const cohorteObj = cohortesRaw.find(c => String(c.año) === String(filtroAnio) && (filtroFormacion === 'TODOS' || c.nombre_formacion === filtroFormacion));
    const cohorteParam = cohorteObj ? cohorteObj.id : filtroAnio;

    Promise.all([
      // 1. Cursos
      axios.get(`${CONFIG.API_URL}/operarEstudiantes.php?cohorte=${cohorteParam}&curso_grupo=0`).catch(() => ({ data: { cursos: [] } })),
      // 2. Estudiantes
      axios.post(`${CONFIG.API_URL}/operarReportes.php`, {
        modo: 'estudiantesPorCurso',
        ciclo: filtroAnio,
        id_nivel: loggeduserNivelId
      }).catch(() => ({ data: { datos: [] } })),
      // 3. Valoraciones / Calificaciones
      axios.post(`${CONFIG.API_URL}/operarReportes.php`, {
        modo: 'valoracionesCohorte',
        ciclo: filtroAnio,
        id_nivel: loggeduserNivelId
      }).catch(() => ({ data: { valoraciones: [] } })),
      // 4. Asistencias por estudiante
      axios.post(`${CONFIG.API_URL}/operarReportes.php`, {
        modo: 'asistenciasEstudiantes',
        ciclo: filtroAnio,
        id_nivel: loggeduserNivelId
      }).catch(() => ({ data: { asistencias: [] } })),
      // 5. Instancias de evaluación
      axios.get(`${CONFIG.API_URL}/operarInstancias.php?id_cohorte=${cohorteParam}`).catch(() => ({ data: { success: false, instancias: [] } }))
    ])
      .then(([resEst, resRep, resVal, resAsis, resInst]) => {
        setCursosRaw(resEst.data && resEst.data.cursos ? resEst.data.cursos : []);
        setEstudiantesRaw(resRep.data && resRep.data.datos ? resRep.data.datos : []);
        setValoracionesRaw(resVal.data && resVal.data.valoraciones ? resVal.data.valoraciones : []);
        setAsistenciasRaw(resAsis.data && resAsis.data.asistencias ? resAsis.data.asistencias : []);

        const instList = (resInst.data && resInst.data.success && Array.isArray(resInst.data.instancias)) ? resInst.data.instancias : [];
        setOpcionesInstanciasRaw(instList);
        setFiltroInstancia('TODAS');
      })
      .catch(err => {
        console.error('Error al cargar datos estadísticos:', err);
        show_alerta('Ocurrió un error al cargar la información estadística', 'error');
      })
      .finally(() => setLoading(false));
  }, [filtroAnio, filtroFormacion, cohortesRaw]);

  // Paso 5: Instancias acorde a la Formación seleccionada
  const opcionesInstancias = useMemo(() => {
    return opcionesInstanciasRaw;
  }, [opcionesInstanciasRaw]);

  // Detectar si la instancia seleccionada es de tipo Valorativo y construir opciones de umbral
  const instanciaSeleccionadaObj = useMemo(() => {
    if (filtroInstancia === 'TODAS') return null;
    return opcionesInstanciasRaw.find(i => String(i.id) === String(filtroInstancia)) || null;
  }, [opcionesInstanciasRaw, filtroInstancia]);

  const esInstanciaValorativa = useMemo(() => {
    if (instanciaSeleccionadaObj) {
      const tipo = String(instanciaSeleccionadaObj.tipoCalificacion || instanciaSeleccionadaObj.tipo_calificacion || '');
      return tipo.toLowerCase().includes('valorativ');
    }
    // Si está en 'TODAS', verificar si existen valoraciones de tipo valorativo en los datos cargados
    return valoracionesRaw.some(v => v.tipo_calificacion && String(v.tipo_calificacion).toLowerCase().includes('valorativ'));
  }, [instanciaSeleccionadaObj, valoracionesRaw]);

  // Opciones de umbral valorativo (ej: ['TED', 'TEP', 'TEA'])
  const opcionesUmbralValorativo = useMemo(() => {
    const tipo = instanciaSeleccionadaObj ? (instanciaSeleccionadaObj.tipoCalificacion || 'Valorativa 2') : 'Valorativa 2';
    const ops = obtenerOpcionesValorativa(tipo, configuracion);
    return ops.filter(op => op && String(op).trim() !== '');
  }, [instanciaSeleccionadaObj, configuracion]);

  // Auto-seleccionar umbral de riesgo por defecto ('TEP' o 'TED' o la 2da opción si existe)
  useEffect(() => {
    if (esInstanciaValorativa && opcionesUmbralValorativo.length > 0) {
      if (!filtroUmbralValorativo || !opcionesUmbralValorativo.includes(filtroUmbralValorativo)) {
        const tepDef = opcionesUmbralValorativo.find(op => op.toUpperCase() === 'TEP' || op.toUpperCase() === 'EP');
        setFiltroUmbralValorativo(tepDef || opcionesUmbralValorativo[Math.min(1, opcionesUmbralValorativo.length - 1)]);
      }
    }
  }, [esInstanciaValorativa, opcionesUmbralValorativo, filtroUmbralValorativo]);

  // ── 8. PROCESAMIENTO DINÁMICO DE DATOS ──
  const datosProcesados = useMemo(() => {
    if (loading) return { estudiantesRiesgo: [], rankingInasistencias: [], datosEspaciosBar: { labels: [], datasets: [] }, kpis: {}, totalEspaciosConBajas: 0 };

    const esCalificacionBajaFiltro = (valor, tipoCal) => {
      if (valor === null || valor === undefined || String(valor).trim() === '') return false;
      const str = String(valor).trim();

      // Si es nota numérica (< 6)
      const num = parseFloat(str);
      if (!isNaN(num) && /^-?\d+(\.\d+)?$/.test(str)) {
        return num < 6;
      }

      // Si es nota valorativa
      const ops = obtenerOpcionesValorativa(tipoCal || 'Valorativa 2', configuracion);
      const valIndex = ops.findIndex(op => op && op.toLowerCase() === str.toLowerCase());

      if (valIndex <= 0) return false;

      // Si el usuario eligió un umbral valorativo (ej. 'TEP')
      if (filtroUmbralValorativo) {
        const umbralIndex = ops.findIndex(op => op && op.toLowerCase() === String(filtroUmbralValorativo).toLowerCase());
        if (umbralIndex > 0) {
          // Alumnos con nivel menor o igual al umbral seleccionado se consideran en riesgo
          return valIndex <= umbralIndex;
        }
      }

      // Por defecto, considerar riesgo si coincide con el nivel más bajo (índice 1)
      return valIndex === 1;
    };

    const mapaEstudiantes = new Map();

    const agregarEstudiante = (idEst, est) => {
      if (!idEst) return null;

      const estFormacion = String(est.nombre_formacion || est.nivel || '').trim().toLowerCase();
      const estOrden = String(est.orden || '').trim().toLowerCase();
      const estDivision = String(est.seccion || est.division || '').trim().toLowerCase();

      // Aplicar filtros acumulativos de formación, orden y división
      if (filtroFormacion !== 'TODOS' && (!estFormacion || estFormacion !== String(filtroFormacion).trim().toLowerCase())) return null;
      if (filtroOrden !== 'TODOS' && (!estOrden || estOrden !== String(filtroOrden).trim().toLowerCase())) return null;
      if (filtroDivision !== 'TODOS' && (!estDivision || estDivision !== String(filtroDivision).trim().toLowerCase())) return null;

      if (!mapaEstudiantes.has(idEst)) {
        mapaEstudiantes.set(idEst, {
          id: idEst,
          nombre: est.nombre || '',
          apellido: est.apellido || '',
          documento: est.documento || '',
          formacion: est.nombre_formacion || est.nivel || 'General',
          orden: est.orden || '-',
          division: est.seccion || est.division || '-',
          desaprobados: [], // Array de { espacio, valor, tipo, id_instancia }
          asistenciaCount: { presentes: 0, ausentes: 0, tarde: 0 }
        });
      }
      return mapaEstudiantes.get(idEst);
    };

    // 1. Registrar estudiantes de la cohorte
    estudiantesRaw.forEach(est => {
      const idEst = String(est.id || est.estudiante_id || est.id_estudiante || est.id_usuario);
      agregarEstudiante(idEst, est);
    });

    // 2. Procesar Valoraciones / Calificaciones (Sólo asociar a estudiantes filtrados y registrados previamente)
    valoracionesRaw.forEach(val => {
      if (filtroInstancia !== 'TODAS' && String(val.id_instancia) !== String(filtroInstancia)) {
        return;
      }

      const idEst = String(val.id_usuario || val.id_estudiante);
      if (!idEst) return;

      const alumno = mapaEstudiantes.get(idEst);
      if (alumno) {
        const esBaja = esCalificacionBajaFiltro(val.valor, val.tipo_calificacion || val.tipoCalificacion);
        if (esBaja) {
          const espacioNombre = val.nombre_espacio || 'Espacio Curricular';
          const yaExiste = alumno.desaprobados.some(d => d.espacio === espacioNombre && String(d.id_instancia) === String(val.id_instancia));
          if (!yaExiste) {
            alumno.desaprobados.push({
              espacio: espacioNombre,
              valor: val.valor,
              tipo: val.tipo_calificacion || val.tipoCalificacion,
              id_instancia: val.id_instancia
            });
          }
        }
      }
    });

    // 3. Procesar Asistencias
    asistenciasRaw.forEach(asis => {
      const idEst = String(asis.id_estudiante || asis.id_usuario);
      const alumno = mapaEstudiantes.get(idEst);
      if (alumno) {
        const estado = String(asis.asistencia || '').toLowerCase();
        const cant = parseInt(asis.cantidad || 1, 10);
        if (estado === 'presente') alumno.asistenciaCount.presentes += cant;
        else if (estado === 'ausente') alumno.asistenciaCount.ausentes += cant;
        else if (estado === 'tarde') alumno.asistenciaCount.tarde += cant;
      }
    });

    const listaAlumnos = Array.from(mapaEstudiantes.values());

    // 4. Estudiantes en Riesgo Académico
    const estudiantesRiesgo = listaAlumnos
      .filter(a => a.desaprobados.length > 0 || a.asistenciaCount.ausentes >= 3)
      .sort((a, b) => b.desaprobados.length - a.desaprobados.length || b.asistenciaCount.ausentes - a.asistenciaCount.ausentes);

    // 5. Ranking de Inasistencias
    const rankingInasistencias = listaAlumnos
      .filter(a => a.asistenciaCount.ausentes > 0)
      .sort((a, b) => b.asistenciaCount.ausentes - a.asistenciaCount.ausentes)
      .slice(0, 15);

    // 6. Conteo para el Gráfico de Espacios Curriculares (Se muestran todos según los filtros activos)
    const mapaEspaciosBar = new Map();

    cursosRaw.forEach(c => {
      const cFormacion = String(c.nombre_formacion || c.nivel_nombre || '').trim().toLowerCase();
      const cOrden = String(c.orden || '').trim().toLowerCase();
      const cDivision = String(c.seccion || c.division || '').trim().toLowerCase();

      const matchF = filtroFormacion === 'TODOS' || (cFormacion && cFormacion === String(filtroFormacion).trim().toLowerCase());
      const matchO = filtroOrden === 'TODOS' || (cOrden && cOrden === String(filtroOrden).trim().toLowerCase());
      const matchD = filtroDivision === 'TODOS' || (cDivision && cDivision === String(filtroDivision).trim().toLowerCase());

      if (matchF && matchO && matchD && c.nombre_espacio) {
        mapaEspaciosBar.set(c.nombre_espacio, 0);
      }
    });

    estudiantesRaw.forEach(e => {
      const eFormacion = String(e.nivel || e.nombre_formacion || '').trim().toLowerCase();
      const eOrden = String(e.orden || '').trim().toLowerCase();
      const eDivision = String(e.division || e.seccion || '').trim().toLowerCase();

      const matchF = filtroFormacion === 'TODOS' || (eFormacion && eFormacion === String(filtroFormacion).trim().toLowerCase());
      const matchO = filtroOrden === 'TODOS' || (eOrden && eOrden === String(filtroOrden).trim().toLowerCase());
      const matchD = filtroDivision === 'TODOS' || (eDivision && eDivision === String(filtroDivision).trim().toLowerCase());

      if (matchF && matchO && matchD && e.nombre_espacio) {
        if (!mapaEspaciosBar.has(e.nombre_espacio)) {
          mapaEspaciosBar.set(e.nombre_espacio, 0);
        }
      }
    });

    // Sumar desaprobados por cada materia
    listaAlumnos.forEach(a => {
      const espaciosAlumnoDesap = new Set(a.desaprobados.map(d => d.espacio));
      espaciosAlumnoDesap.forEach(espacio => {
        const countActual = mapaEspaciosBar.get(espacio) || 0;
        mapaEspaciosBar.set(espacio, countActual + 1);
      });
    });

    const espaciosOrdenados = Array.from(mapaEspaciosBar.entries())
      .map(([espacio, cantidad]) => ({ espacio, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad || a.espacio.localeCompare(b.espacio));

    const labelsEspacios = espaciosOrdenados.map(e => e.espacio);
    const dataEspacios = espaciosOrdenados.map(e => e.cantidad);

    const datosEspaciosBar = {
      labels: labelsEspacios,
      datasets: [
        {
          label: 'Cantidad de Estudiantes en Riesgo',
          data: dataEspacios,
          backgroundColor: dataEspacios.map(v => v > 0 ? 'rgba(239, 68, 68, 0.85)' : 'rgba(59, 130, 246, 0.5)'),
          borderColor: dataEspacios.map(v => v > 0 ? 'rgba(220, 38, 38, 1)' : 'rgba(37, 99, 235, 0.8)'),
          borderWidth: 1,
          borderRadius: 6,
          hoverBackgroundColor: dataEspacios.map(v => v > 0 ? 'rgba(220, 38, 38, 1)' : 'rgba(37, 99, 235, 1)')
        }
      ]
    };

    const totalCriticoRiesgo = listaAlumnos.filter(a => a.desaprobados.length > 3).length;
    const totalDesaprobadosCount = listaAlumnos.reduce((acc, curr) => acc + curr.desaprobados.length, 0);
    const primerEspacioCritico = espaciosOrdenados.find(e => e.cantidad > 0);
    const espacioMasCritico = primerEspacioCritico ? `${primerEspacioCritico.espacio} (${primerEspacioCritico.cantidad})` : 'Sin estudiantes en riesgo (0)';

    return {
      estudiantesRiesgo,
      rankingInasistencias,
      datosEspaciosBar,
      totalEspaciosConBajas: espaciosOrdenados.length,
      kpis: {
        totalMonitoreados: listaAlumnos.length,
        totalRiesgo: totalCriticoRiesgo > 0 ? totalCriticoRiesgo : estudiantesRiesgo.length,
        espacioMasCritico,
        totalDesaprobadosCount
      }
    };
  }, [
    estudiantesRaw,
    valoracionesRaw,
    asistenciasRaw,
    cursosRaw,
    filtroFormacion,
    filtroOrden,
    filtroDivision,
    filtroInstancia,
    filtroUmbralValorativo,
    loading,
    configuracion
  ]);

  // Opciones para el gráfico de barras Chart.js
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { family: 'inherit', size: 13, weight: '600' },
          color: '#334155'
        }
      },
      tooltip: {
        backgroundColor: '#0f172a',
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => ` ${context.raw} estudiante(s) en riesgo`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 12 }, color: '#475569' }
      },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 12 }, color: '#475569' },
        grid: { color: 'rgba(226, 232, 240, 0.8)' }
      }
    }
  };

  return (
    <div className="container-principal mon-container">
      {/* ── ENCABEZADO ── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h3 className="mon-title m-0">
            <i className="fa-solid fa-chart-line text-primary me-2" ></i>
            Monitoreo y Estadística Institucional
          </h3>
          <p className="text-muted small m-0 mt-1">
            Análisis de datos, riesgo académico e inasistencias para la toma de decisiones.
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm mt-2 mt-sm-0" onClick={() => window.print()}>
          <i className="fa-solid fa-print me-1"></i> Imprimir Reporte
        </button>
      </div>

      {/* ── PANEL DE FILTROS EN CASCADA ── */}
      <div className="card shadow-sm border-0 mb-4 mon-filter-card">
        <div className="card-body p-3 bg-light rounded border">
          <div className="row g-2 align-items-center">
            {/* 1. Selector de Cohorte / Año (Muestra solo los años disponibles según nivel) */}
            <div className="col-12 col-sm-6 col-md-2">
              <label className="form-label fw-bold text-secondary small mb-1">
                <i className="fa-solid fa-calendar-days me-1 text-primary"></i> Cohorte / Ciclo
              </label>
              <select
                className="form-select form-select-sm"
                value={filtroAnio}
                onChange={e => setFiltroAnio(e.target.value)}
              >
                {opcionesAnios.map(anio => (
                  <option key={anio} value={anio}>
                    {anio}
                  </option>
                ))}
              </select>
            </div>

            {/* 2. Selector de Formación (Habilitadas en la cohorte seleccionada) */}
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label fw-bold text-secondary small mb-1">
                <i className="fa-solid fa-graduation-cap me-1 text-primary"></i> Formación
              </label>
              <select
                className="form-select form-select-sm"
                value={filtroFormacion}
                onChange={e => setFiltroFormacion(e.target.value)}
              >
                <option value="TODOS">Todas las Formaciones</option>
                {opcionesFormaciones.map((f, idx) => (
                  <option key={idx} value={f}>{f}</option>
                ))}
              </select>
            </div>

            {/* 3. Selector de Año / Orden (Correspondientes a la formación seleccionada + Opción Todos) */}
            <div className="col-12 col-sm-6 col-md-2">
              <label className="form-label fw-bold text-secondary small mb-1">
                <i className="fa-solid fa-layer-group me-1 text-primary"></i> Año / Orden
              </label>
              <select
                className="form-select form-select-sm"
                value={filtroOrden}
                onChange={e => setFiltroOrden(e.target.value)}
              >
                <option value="TODOS">Todos los Años</option>
                {opcionesOrdenes.map((o, idx) => (
                  <option key={idx} value={o}>{o}</option>
                ))}
              </select>
            </div>

            {/* 4. Selector de División (Habilitado si no es TODOS los años) */}
            <div className="col-12 col-sm-6 col-md-2">
              <label className="form-label fw-bold text-secondary small mb-1">
                <i className="fa-solid fa-users-rectangle me-1 text-primary"></i> División
              </label>
              <select
                className="form-select form-select-sm"
                value={filtroDivision}
                onChange={e => setFiltroDivision(e.target.value)}
                disabled={filtroOrden === 'TODOS'}
              >
                <option value="TODOS">Todas las Div.</option>
                {opcionesDivisiones.map((d, idx) => (
                  <option key={idx} value={d}>División {d}</option>
                ))}
              </select>
            </div>

            {/* 5. Selector de Instancia (Acorde a la formación seleccionada) */}
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label fw-bold text-secondary small mb-1">
                <i className="fa-solid fa-clock me-1 text-primary"></i> Instancia
              </label>
              <select
                className="form-select form-select-sm"
                value={filtroInstancia}
                onChange={e => setFiltroInstancia(e.target.value)}
              >
                <option value="TODAS">Todas las Instancias</option>
                {opcionesInstancias.map(inst => (
                  <option key={inst.id} value={inst.id}>
                    {inst.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* 6. DESPLEGABLE ADICIONAL DE UMBRAL DE RIESGO VALORATIVO (Visible si aplica valoración) */}
            {esInstanciaValorativa && opcionesUmbralValorativo.length > 0 && (
              <div className="col-12 col-sm-6 col-md-3 mt-2">
                <label className="form-label fw-bold text-danger small mb-1">
                  <i className="fa-solid fa-triangle-exclamation me-1 text-danger"></i>
                  Riesgo valorativo a partir de:
                </label>
                <select
                  className="form-select form-select-sm border-danger text-danger fw-semibold"
                  value={filtroUmbralValorativo}
                  onChange={e => setFiltroUmbralValorativo(e.target.value)}
                >
                  {opcionesUmbralValorativo.map((op, idx) => (
                    <option key={idx} value={op}>
                      {op} (e inferiores)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 text-muted fw-semibold">Cargando métricas e información estadística...</p>
        </div>
      ) : (
        <>
          {/* ── RESUMEN TARJETAS KPI (ESTÉTICA SOBRIA UNIFORME TIPO AYUDA CONTEXTUAL) ── */}
          <div className="row g-3 mb-4">
            {/* Card 1: Estudiantes Monitoreados */}
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card mon-kpi-card h-100 p-3" style={{ borderColor: `${(configuracion && configuracion.color_primario) || '#014751'}33` }}>
                <div className="d-flex align-items-center">
                  <div
                    className="mon-kpi-icon-box me-3 shadow-sm"
                    style={{
                      backgroundColor: `${(configuracion && configuracion.color_primario) || '#014751'}12`,
                      color: (configuracion && configuracion.color_primario) || '#014751'
                    }}
                  >
                    <i className="fa-solid fa-user-group fa-lg"></i>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="mon-kpi-label">Estudiantes Monitoreados</div>
                    <div className="mon-kpi-value">{datosProcesados.kpis.totalMonitoreados || 0}</div>
                    <div className="mt-1">
                      <span className="badge bg-light text-secondary border fw-normal" style={{ fontSize: '0.7rem' }}>
                        Matriculados activos
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Estudiantes en Riesgo */}
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card mon-kpi-card h-100 p-3" style={{ borderColor: `${(configuracion && configuracion.color_primario) || '#014751'}33` }}>
                <div className="d-flex align-items-center">
                  <div
                    className="mon-kpi-icon-box me-3 shadow-sm"
                    style={{
                      backgroundColor: `${(configuracion && configuracion.color_primario) || '#014751'}12`,
                      color: (configuracion && configuracion.color_primario) || '#014751'
                    }}
                  >
                    <i className="fa-solid fa-triangle-exclamation fa-lg"></i>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="mon-kpi-label">Estudiantes en Riesgo</div>
                    <div
                      className="mon-kpi-value"
                      style={{ color: datosProcesados.kpis.totalRiesgo > 0 ? '#dc2626' : '#0f172a' }}
                    >
                      {datosProcesados.kpis.totalRiesgo || 0}
                    </div>
                    <div className="mt-1">
                      <span
                        className={`badge ${datosProcesados.kpis.totalRiesgo > 0 ? 'bg-danger-subtle text-danger border border-danger-subtle' : 'bg-light text-secondary border'} fw-normal`}
                        style={{ fontSize: '0.7rem' }}
                      >
                        {datosProcesados.kpis.totalRiesgo > 0 ? 'Atención requerida' : 'Sin alertas'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3: Espacio Crítico */}
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card mon-kpi-card h-100 p-3" style={{ borderColor: `${(configuracion && configuracion.color_primario) || '#014751'}33` }}>
                <div className="d-flex align-items-center">
                  <div
                    className="mon-kpi-icon-box me-3 shadow-sm"
                    style={{
                      backgroundColor: `${(configuracion && configuracion.color_primario) || '#014751'}12`,
                      color: (configuracion && configuracion.color_primario) || '#014751'
                    }}
                  >
                    <i className="fa-solid fa-chalkboard-user fa-lg"></i>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="mon-kpi-label">Espacio más Crítico</div>
                    <div
                      className="mon-kpi-value fs-6 text-truncate"
                      style={{ maxWidth: '160px' }}
                      title={datosProcesados.kpis.espacioMasCritico}
                    >
                      {datosProcesados.kpis.espacioMasCritico || 'Sin riesgo'}
                    </div>
                    <div className="mt-1">
                      <span className="badge bg-light text-secondary border fw-normal" style={{ fontSize: '0.7rem' }}>
                        Mayor incidencia
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 4: Total Calificaciones Bajas */}
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card mon-kpi-card h-100 p-3" style={{ borderColor: `${(configuracion && configuracion.color_primario) || '#014751'}33` }}>
                <div className="d-flex align-items-center">
                  <div
                    className="mon-kpi-icon-box me-3 shadow-sm"
                    style={{
                      backgroundColor: `${(configuracion && configuracion.color_primario) || '#014751'}12`,
                      color: (configuracion && configuracion.color_primario) || '#014751'
                    }}
                  >
                    <i className="fa-solid fa-list-check fa-lg"></i>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="mon-kpi-label">Calificaciones Bajas</div>
                    <div className="mon-kpi-value">{datosProcesados.kpis.totalDesaprobadosCount || 0}</div>
                    <div className="mt-1">
                      <span className="badge bg-light text-secondary border fw-normal" style={{ fontSize: '0.7rem' }}>
                        En el período
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── DOS COLUMNAS: RIESGO ACADÉMICO E INASISTENCIAS ── */}
          <div className="row g-4 mb-4">
            {/* COLUMNA IZQUIERDA: ESTUDIANTES EN RIESGO ACADÉMICO */}
            <div className="col-12 col-lg-7">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-header bg-white py-2 px-3 border-bottom d-flex justify-content-between align-items-center">
                  <h6 className="m-0 fw-bold text-danger" style={{ fontSize: '0.9rem' }}>
                    <i className="fa-solid fa-triangle-exclamation me-2"></i>
                    Estudiantes en Riesgo Académico
                  </h6>
                  <span className="badge bg-danger rounded-pill" style={{ fontSize: '0.75rem' }}>
                    {datosProcesados.estudiantesRiesgo.length}
                  </span>
                </div>
                <div className="card-body p-0">
                  {datosProcesados.estudiantesRiesgo.length === 0 ? (
                    <div className="p-4 text-center text-muted">
                      <i className="fa-solid fa-circle-check text-success fa-2x mb-2 d-block"></i>
                      No se registraron estudiantes con riesgo académico o ausencias en los filtros seleccionados.
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light sticky-top" style={{ fontSize: '0.8rem' }}>
                          <tr>
                            <th>Estudiante</th>
                            <th>Curso / Div</th>
                            <th className="text-center">Materias en Riesgo</th>
                            <th className="text-center">Inasistencias</th>
                          </tr>
                        </thead>
                        <tbody>
                          {datosProcesados.estudiantesRiesgo.map((est, idx) => (
                            <tr key={idx}>
                              <td>
                                <div className="fw-semibold text-dark" style={{ fontSize: '0.82rem', lineHeight: '1.2' }}>
                                  {(est.apellido || '').toUpperCase()} {est.nombre}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{est.documento}</div>
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>
                                  {est.orden} - {est.division}
                                </span>
                              </td>
                              <td className="text-center">
                                {est.desaprobados.length > 3 ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger d-inline-flex align-items-center gap-1 shadow-sm px-2 py-1"
                                    style={{ fontSize: '0.72rem', borderRadius: '6px' }}
                                    title="Haz clic para ver la lista de materias en riesgo"
                                    onClick={() => {
                                      setEstudianteSeleccionadoModal(est);
                                      setMostrarModalEspacios(true);
                                    }}
                                  >
                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                    Crítico ({est.desaprobados.length})
                                    <i className="fa-solid fa-eye ms-1 opacity-75"></i>
                                  </button>
                                ) : est.desaprobados.length > 0 ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-warning text-dark d-inline-flex align-items-center gap-1 shadow-sm px-2 py-1"
                                    style={{ fontSize: '0.72rem', borderRadius: '6px' }}
                                    title="Haz clic para ver la lista de materias en riesgo"
                                    onClick={() => {
                                      setEstudianteSeleccionadoModal(est);
                                      setMostrarModalEspacios(true);
                                    }}
                                  >
                                    <i className="fa-solid fa-book-bookmark"></i>
                                    {est.desaprobados.length} materia(s)
                                    <i className="fa-solid fa-eye ms-1 opacity-75"></i>
                                  </button>
                                ) : (
                                  <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1" style={{ fontSize: '0.72rem' }}>
                                    0 materias
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge ${est.asistenciaCount.ausentes > 10 ? 'bg-danger' : est.asistenciaCount.ausentes > 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}
                                  style={{ fontSize: '0.72rem' }}
                                >
                                  {est.asistenciaCount.ausentes} Ausentes
                                </span>
                                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                  Pres: {est.asistenciaCount.presentes}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* COLUMNA DERECHA: RANKING DE INASISTENCIAS */}
            <div className="col-12 col-lg-5">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-header bg-white py-2 px-3 border-bottom d-flex justify-content-between align-items-center">
                  <h6 className="m-0 fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                    <i className="fa-solid fa-user-xmark text-warning me-2"></i>
                    Mayor Cantidad de Inasistencias
                  </h6>
                  <span className="badge bg-secondary rounded-pill" style={{ fontSize: '0.75rem' }}>Top 15</span>
                </div>
                <div className="card-body p-0">
                  {datosProcesados.rankingInasistencias.length === 0 ? (
                    <div className="p-4 text-center text-muted">
                      <i className="fa-solid fa-clipboard-check text-muted fa-2x mb-2 d-block"></i>
                      No hay registros de inasistencias para el filtro seleccionado.
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light sticky-top" style={{ fontSize: '0.8rem' }}>
                          <tr>
                            <th>Estudiante</th>
                            <th>Curso</th>
                            <th className="text-center">Ausentes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {datosProcesados.rankingInasistencias.map((est, idx) => (
                            <tr key={idx}>
                              <td>
                                <div className="fw-semibold text-dark" style={{ fontSize: '0.82rem', lineHeight: '1.2' }}>
                                  {(est.apellido || '').toUpperCase()} {est.nombre}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{est.documento}</div>
                              </td>
                              <td>
                                <span className="text-secondary" style={{ fontSize: '0.72rem' }}>{est.orden} {est.division}</span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-danger px-2 py-1" style={{ fontSize: '0.75rem' }}>
                                  {est.asistenciaCount.ausentes}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── GRÁFICO DE BARRAS ABAJO: ESTUDIANTES EN RIESGO POR ESPACIO CURRICULAR ── */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center">
              <div>
                <h5 className="m-0 fw-bold text-dark">
                  <i className="fa-solid fa-chart-bar text-primary me-2"></i>
                  Cantidad de Estudiantes en Riesgo por Espacio Curricular
                </h5>
                <small className="text-muted">
                  Criterio: Calificación numérica menor a 6 o calificación valorativa menor o igual al umbral seleccionado ({filtroUmbralValorativo || 'Índice 1'}).
                </small>
              </div>
              <span className="badge bg-primary-subtle text-primary border border-primary px-3 py-1 mt-2 mt-sm-0">
                {datosProcesados.totalEspaciosConBajas} Espacios Curriculares
              </span>
            </div>
            <div className="card-body p-4">
              {datosProcesados.datosEspaciosBar.labels.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fa-solid fa-chart-simple fa-3x text-secondary mb-3 d-block"></i>
                  No se encontraron espacios curriculares configurados en los filtros seleccionados.
                </div>
              ) : (
                <div className="mon-chart-wrapper" style={{ minHeight: '380px', height: `${Math.max(380, datosProcesados.datosEspaciosBar.labels.length * 28)}px` }}>
                  <Bar data={datosProcesados.datosEspaciosBar} options={chartOptions} />
                </div>
              )}
            </div>
          </div>

          {/* ── MODAL DETALLE DE MATERIAS EN RIESGO POR ESTUDIANTE ── */}
          <Modal
            show={mostrarModalEspacios}
            onHide={() => setMostrarModalEspacios(false)}
            centered
            size="lg"
            className="fade"
          >
            <Modal.Header closeButton className="border-bottom py-3 px-4" style={{ backgroundColor: '#f8fafc' }}>
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center me-2 shadow-sm"
                  style={{
                    width: '42px',
                    height: '42px',
                    backgroundColor: `${(configuracion && configuracion.color_primario) || '#014751'}15`,
                    color: (configuracion && configuracion.color_primario) || '#014751'
                  }}
                >
                  <i className="fa-solid fa-book-open-reader fs-5"></i>
                </div>
                <div>
                  <Modal.Title className="h6 fw-bold mb-0 text-dark">
                    Detalle de Materias en Riesgo Académico
                  </Modal.Title>
                  {estudianteSeleccionadoModal && (
                    <span className="text-muted small">
                      Estudiante: <strong className="text-dark">{(estudianteSeleccionadoModal.apellido || '').toUpperCase()} {estudianteSeleccionadoModal.nombre}</strong>
                    </span>
                  )}
                </div>
              </div>
            </Modal.Header>
            <Modal.Body className="p-4">
              {estudianteSeleccionadoModal && (
                <>
                  <div className="d-flex flex-wrap align-items-center justify-content-between p-3 mb-4 rounded-3 border bg-light gap-2">
                    <div>
                      <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: '0.7rem' }}>Documento</div>
                      <div className="fw-bold text-dark">{estudianteSeleccionadoModal.documento || '-'}</div>
                    </div>
                    <div>
                      <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: '0.7rem' }}>Curso / División</div>
                      <span className="badge bg-white text-dark border px-2 py-1 fw-bold">
                        {estudianteSeleccionadoModal.orden} - {estudianteSeleccionadoModal.division}
                      </span>
                    </div>
                    <div>
                      <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: '0.7rem' }}>Inasistencias</div>
                      <span className={`badge ${estudianteSeleccionadoModal.asistenciaCount.ausentes > 10 ? 'bg-danger' : estudianteSeleccionadoModal.asistenciaCount.ausentes > 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                        {estudianteSeleccionadoModal.asistenciaCount.ausentes} Ausentes
                      </span>
                    </div>
                    <div>
                      <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: '0.7rem' }}>Total en Riesgo</div>
                      <span className={`badge ${estudianteSeleccionadoModal.desaprobados.length > 3 ? 'bg-danger' : 'bg-warning text-dark'} fs-6`}>
                        {estudianteSeleccionadoModal.desaprobados.length} materia(s)
                      </span>
                    </div>
                  </div>

                  <h6 className="fw-bold text-dark mb-3">
                    <i className="fa-solid fa-list-check me-2" style={{ color: (configuracion && configuracion.color_primario) || '#014751' }}></i>
                    Espacios Curriculares Afectados
                  </h6>

                  {estudianteSeleccionadoModal.desaprobados.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="fa-solid fa-circle-check text-success fa-2x mb-2 d-block"></i>
                      El estudiante no presenta materias en riesgo.
                    </div>
                  ) : (
                    <div className="table-responsive border rounded-3 overflow-hidden">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light" style={{ fontSize: '0.8rem' }}>
                          <tr>
                            <th className="text-center" style={{ width: '40px' }}>#</th>
                            <th>Espacio Curricular / Materia</th>
                            <th className="text-center">Instancia</th>
                            <th className="text-center">Calificación</th>
                            <th className="text-center">Condición</th>
                          </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.85rem' }}>
                          {[...estudianteSeleccionadoModal.desaprobados]
                            .sort((a, b) => (a.espacio || '').localeCompare(b.espacio || ''))
                            .map((item, idx) => {
                              const instObj = opcionesInstanciasRaw.find(i => String(i.id) === String(item.id_instancia));
                              const nombreInstancia = instObj ? (instObj.nombre_instancia || instObj.nombre) : (item.id_instancia ? `Instancia #${item.id_instancia}` : 'General');
                              return (
                                <tr key={idx}>
                                  <td className="text-center fw-semibold text-muted">{idx + 1}</td>
                                  <td>
                                    <div className="fw-bold text-dark">{item.espacio}</div>
                                    {item.tipo && (
                                      <span className="text-muted small" style={{ fontSize: '0.72rem' }}>Escala: {item.tipo}</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-light text-dark border fw-normal" style={{ fontSize: '0.75rem' }}>
                                      {nombreInstancia}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-1 fs-6 fw-bold">
                                      {item.valor}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-danger fw-normal" style={{ fontSize: '0.75rem' }}>
                                      En Riesgo
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer className="border-top py-2 px-4 bg-light">
              <Button variant="secondary" size="sm" onClick={() => setMostrarModalEspacios(false)}>
                <i className="fa-solid fa-xmark me-1"></i> Cerrar
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
}

export default MonitoreoEstadisticas;
